import React from 'react';
import T from './tokens';

/**
 * Shared Recharts styling. Spread these props onto chart primitives so every
 * chart gets the same gridlines, axes, cursors and gradients.
 *
 *   <CartesianGrid {...chartGrid} />
 *   <XAxis {...chartAxis} dataKey="label" />
 *   <YAxis {...chartAxis} />
 */

export const CHART_FONT = T.font.sans;

export const chartGrid = {
  stroke: '#E8EBF2',
  strokeDasharray: '3 5',
  vertical: false,
} as const;

export const chartAxisTick = { fontSize: 11.5, fill: T.text.tertiary, fontFamily: CHART_FONT, fontWeight: 500 } as const;

export const chartAxis = {
  tick: chartAxisTick,
  tickLine: false,
  axisLine: false,
  tickMargin: 10,
} as const;

/** X axis with a hairline baseline. */
export const chartXAxis = {
  ...chartAxis,
  axisLine: { stroke: '#E2E6EE' },
} as const;

export const chartCursorLine = { stroke: T.color.brand[300], strokeWidth: 1.5, strokeDasharray: '4 4' } as const;
export const chartCursorBar = { fill: 'rgba(79,70,229,0.06)', radius: 8 } as const;

/** Pass-mark reference line: dashed red with a small label. */
export const passLineProps = {
  stroke: T.color.danger.solid,
  strokeDasharray: '5 5',
  strokeOpacity: 0.7,
  strokeWidth: 1.25,
} as const;

export const passLineLabel = (text: string, position: 'right' | 'insideTopRight' | 'insideBottomRight' | 'top' = 'insideTopRight') => ({
  value: text,
  position,
  fill: T.color.danger.text,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: CHART_FONT,
});

/** Halo dot for the hovered point on a line chart. */
export const activeDotHalo = (color: string = T.color.brand[600]) => ({
  r: 6,
  fill: color,
  stroke: '#FFFFFF',
  strokeWidth: 3,
  style: { filter: `drop-shadow(0 0 6px ${color}66)` },
});

/** Vertical area gradient — put inside the chart, reference with `url(#id)`. */
export const AreaGradient: React.FC<{ id: string; color: string; from?: number; to?: number }> = ({ id, color, from = 0.28, to = 0 }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={from} />
      <stop offset="100%" stopColor={color} stopOpacity={to} />
    </linearGradient>
  </defs>
);

/** Animation timings for Recharts series (kept under 600ms). */
export const chartAnimation = {
  isAnimationActive: true,
  animationDuration: 550,
  animationEasing: 'ease-out' as const,
};

/** Respect prefers-reduced-motion for chart animations. */
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const seriesAnimation = () => (prefersReducedMotion() ? { isAnimationActive: false } : chartAnimation);

/** Tooltip wrapper props: allow escaping the chart box so tooltips never clip. */
export const tooltipWrapper = {
  allowEscapeViewBox: { x: false, y: true },
  wrapperStyle: { outline: 'none', zIndex: 30 },
} as const;
